import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
}                         from 'react';
import styled             from 'styled-components';
import moment             from 'moment';
import {
  TDataTableRow,
  ISearchInputValues,
}                         from '@typings/components';
import {
  IManager,
  IManagersFacetsStats,
  TManagerFilterOption,
}                         from '@typings/accountManagement';
import { EAccountPage }   from '@enums/accountManagement';
import {
  EAppPage,
  EUserRole,
}                         from '@enums/common';
import {
  useFetchManagers,
  useFetchManagersByFilter,
}                         from '@modules/accountManagement/queries/accountQueries';
import {
  formatFullName,
  parseRecords,
}                         from '@utils/commonFunctions';
import {
  USER_STATUS_NAME,
  ROWS_PER_PAGE,
}                         from '@utils/suggestions';
import { USER_ROLE_NAME } from '@modules/accountManagement/utils/suggestions';
import { LinksWrapper }   from '@components/LinksWrapper/LinksWrapper';
import { Loader }         from '@components/Loader/Loader';
import { LinkButton }     from '@components/LinkButton/LinkButton';
import { UsersPage }      from '@modules/accountManagement/pages/UsersPage';

const Wrapper = styled.div`
  padding: 20px 30px 60px;

  @media (min-width: 1280px) {
    padding: 20px 100px 60px;
  }
`;

interface IProps {
  push: (path: string) => void;
}

const getFilterData = (
  filterValue: string,
) =>
  (filterValue !== 'all'
    ? [{
      propertyName : 'CreditUnion/Name',
      operator     : 'eq',
      value        : filterValue,
    }]
    : []
  );

export const UsersContainer: React.FC<IProps> = ({
  push,
}) => {
  const [users, setUsers]                             = useState<IManager[]>([]);
  const [filterOptions, setFilterOptions]             = useState<IManagersFacetsStats>({});
  const [totalRecords, setTotalRecords]               = useState(0);
  const [selectedFilterValue, setSelectedFilterValue] = useState('all');
  const searchQuery                                   = useRef('');
  const currentPage                                   = useRef(0);

  const { refetch: fetchAllUsers, isLoading } = useFetchManagers({
    enabled   : false,
    onSuccess : (data) => {
      const updatedData: IManager[] = parseRecords<IManager>(data);

      setFilterOptions(data?.facetsStats);
      setUsers(updatedData);
      setTotalRecords(data?.totalRecordsFound);
    },
  });

  const { mutate: fetchUsersByFilter } = useFetchManagersByFilter({
    onSuccess: (data) => {
      const updatedData: IManager[] = parseRecords<IManager>(data);

      setUsers(updatedData);
      setTotalRecords(data?.totalRecordsFound);
    },
  });

  const handleManagersFetch = useCallback(
    (
      offset?     : number,
      limit?      : number,
      searchText? : string,
      filters?    : any,
    ) => {
      let query = `?Skip=${offset}&Size=${limit}`;

      if (searchText) {
        query += `&SearchText=${searchText}`;
      }

      if (filters?.length) {
        const { propertyName, operator, value } = filters[0];
        query += `&Filters=${propertyName} ${operator} '${value}'`;
      }

      fetchUsersByFilter(query);
    },
    [fetchUsersByFilter],
  );

  const handleSearchText = useCallback(
    ({ searchText }: ISearchInputValues): void => {
      searchQuery.current = searchText;
      currentPage.current = 0;

      handleManagersFetch(
        0,
        ROWS_PER_PAGE,
        searchText || '*',
        getFilterData(selectedFilterValue),
      );
    },
    [handleManagersFetch, selectedFilterValue],
  );

  const handleChangePage = useCallback(
    (pageNumber: number): void => {
      currentPage.current = pageNumber;

      handleManagersFetch(
        pageNumber * ROWS_PER_PAGE,
        ROWS_PER_PAGE,
        searchQuery.current || '*',
        getFilterData(selectedFilterValue),
      );
    },
    [handleManagersFetch, selectedFilterValue],
  );

  const handleSelectFilterValue = useCallback(
    ({ value }: any): void => {
      const filter: any = getFilterData(value);

      handleManagersFetch(
        0,
        50,
        '',
        filter,
      );

      setSelectedFilterValue(value);
    },
    [handleManagersFetch],
  );

  const filterValues = useMemo<TManagerFilterOption[]>(
    () => {
      const allManagersCount = (filterOptions?.['creditUnion.Name'] || []).reduce(
        (acc: number, cur: TManagerFilterOption): number => acc + cur.count, 0,
      );

      if (allManagersCount) {
        const defaultOption = [{
          label: {
            status : 'Default - All -',
            count  : `(${allManagersCount})`,
          },
          value: 'all',
        }];

        return (filterOptions?.['creditUnion.Name'] ?? []).reduce((acc: any, cur: any): any => ([
          ...acc,
          {
            label: {
              status : cur.value,
              count  : `(${cur.count})`,
            },
            value: cur.value,
          },
        ]), defaultOption);
      }

      return [];
    },
    [filterOptions],
  );

  useEffect(() => {
    if (!users.length) {
      fetchAllUsers();
    }
  }, [fetchAllUsers, users.length]);

  const getItemRows = (
    data  : IManager[],
  ): TDataTableRow[] =>
    data.reduce(
      (itemRows: TDataTableRow[], user: IManager): TDataTableRow[] => {
        const row: TDataTableRow = [
          {
            value : `${formatFullName(user)}`,
            align : 'left',
          },
          {
            value : `${(user?.roles ?? []).map((role: EUserRole) => USER_ROLE_NAME[role]).join(', ')}`,
            align : 'left',
          },
          {
            value : `${USER_STATUS_NAME[user?.status]}`,
            align : 'left',
          },
          {
            value : user?.lastLogin ? `${moment(user?.lastLogin).format('MM/DD/YY')}` : '',
            align : 'left',
          },
        ];

        itemRows.push(row);

        return itemRows;
      }, [],
    );

  const tableRows = useMemo(
    (): TDataTableRow[] => getItemRows(users),
    [users],
  );

  return (
    <Wrapper>
      <LinksWrapper>
        <LinkButton
          label   = "Return to Account Management"
          onClick = {(): void => push(`${EAppPage.AccountManagement}/${EAccountPage.Search}`)}
        />
      </LinksWrapper>

      {isLoading ? <Loader /> : (
        <UsersPage
          tableRows               = {tableRows}
          filterValues            = {filterValues}
          handleSelectFilterValue = {handleSelectFilterValue}
          selectedFilterValue     = {selectedFilterValue}
          inputValue              = {searchQuery.current}
          handleSearch            = {handleSearchText}
          currentPage             = {currentPage.current}
          totalAuctionsCount      = {totalRecords}
          handleChangePage        = {handleChangePage}
          rowsPerPage             = {ROWS_PER_PAGE}
        />
      )}
    </Wrapper>
  );
};
